import config             from '../config';
import AuthService        from './AuthService';
import { throttleGlobal } from './ThrottleGlobal';
import { STORAGE_KEYS }   from './keys.js';
import CommonUtilities    from './CommonUtilities';

const verifyCognitoSessionThrottled = throttleGlobal('verifyCognitoSession', AuthService.verifyCognitoSession, 60000);
const truncateToken = CommonUtilities.truncateToken;

export default class FetchService {

    static debug = !!config?.DEBUG?.FetchService;

    static async asyncGet( oRequestParameters ) {
        return await this.asyncRequest({ method: 'GET',  ...oRequestParameters });
    }
    static async asyncPost( oRequestParameters ) {
        return await this.asyncRequest({ method: 'POST', ...oRequestParameters });
    }

    static async asyncRequest(
        {
             method  = 'GET'
            ,url
            ,params  = ''
            ,type
            ,encode
            ,headers = {
                 'Accept':       'application/json'
                ,'Content-Type': 'application/json'
            }
            ,displayErrorHandler
        }
    ){

        const bDebug = this.debug;
        const isErrorHandlerNotNull = !!displayErrorHandler;
        if ( typeof displayErrorHandler !== 'function' ) {
            displayErrorHandler = (error) => { console.warn('nessun displayErrorHandler impostato.'); console.error(error); }
        }
        
        headers['Cache-Control']  ='no-cache, no-store, must-revalidate';
        headers['Pragma'       ]  ='no-cache';
        headers['Expires'      ]  ='0';

        let responseToReturn      = '';
        let sExpectedErrorToShow  = '';
        const sDefaultErrorToShow = 'Connection or application error.';

        bDebug && console.log(url);
        
        encode = encode || '';

        try {

        // --- sezione COGNITO ---

            // Prima di iniziare la richiesta, controlla se il token sta per scadere (ed eventualmente lo rinnova)
            if ( ( config?.COGNITO_ENABLED === 'Y' ) && ( config?.COGNITO_RENEW_ACCESS_ENABLED === 'Y' ) ) {
                try {
                    const oVerifyResult = await verifyCognitoSessionThrottled(); // limitata a 1 volta al minuto al massimo
                    if ( oVerifyResult?.ignored ) {
                        // bDebug && console.warn( oVerifyResult?.message );
                    } else if ( !oVerifyResult?.isValid || !oVerifyResult?.accessToken ) {
                        // se non valida o comunque non viene restituito il token
                        throw new Error('No Cognito Access Token');
                    }
                } catch (err) {
                    console.error('Errore nel controllo preventivo scadenza Cognito Access token', err);
                    // Continua comunque con la richiesta, il resto della funzione gestirà eventuali 401
                    // TODO valutare logout
                }
            }

        // --- sezione CONTROLLI preventivi ---

            if ( bDebug && !url )       { console.error('no url specified calling FetchService'); }

            let token                  = localStorage.getItem( STORAGE_KEYS.TOKEN );
            if ( bDebug && !token )     { console.error('no token in localStorage'); }

            headers['Authorization']   = token ? `Bearer ${token}` : '';

            const env                  = config.ENVIRONMENTS[ process.env.REACT_APP_ENV || 'undefined' ];
            if ( bDebug && !env )       { console.error('no valid env found'); }

        // --- sezione PREPARAZIONE della chiamata ---
            // Aggiunta origin come params di URL per poterci collegare da local su ambienti remoti con Middleware
            const apiUrl               = new URL( ( ( env || {} ).API_URL || config.API_URL ) + url, window.location.origin );
            const objRequest           = { method ,headers ,cache: 'no-store' }; // IMPORTANTE cache:'no-store' per dire al browser di NON usare la cache 

            // RIMOSSO // .credentials perchè nel backend utilizziamo solo "JWT token" e non res.append( "Access-Control-Allow-Credentials" , "true" )
            // RIMOSSO // if ( config?.CONTAINER_ENABLED !== 'Y' ) { objRequest.credentials = 'include'; }

            if ( params ) {
                if ( method === 'GET') {
                    Object.keys(params).forEach( paramName => apiUrl.searchParams.append(paramName, params[paramName]) );
                } else if ( method === 'POST') {
                    objRequest.body = JSON.stringify( params );
                }
            }

        // --- sezione CHIAMATA ---
            let response = await fetch( apiUrl, objRequest );

        // --- sezione RISPOSTA ---

            // RISPOSTA ERRATA

            // specifico errore 401 intercettato (credenziali non valide o token scaduto)
            if ( response.status === 401 ) {
                sExpectedErrorToShow = 'Invalid credentials or user not enabled';
                console.error('Token expired');
                await AuthService.logout();
                throw new Error('Token expired');
            }

            // specifico errore 403 intercettato (non autorizzato)
            if ( response.status === 403 ) {
                sExpectedErrorToShow = 'Not authorized';
                console.error(sExpectedErrorToShow);
                await AuthService.logout();
                throw new Error(sExpectedErrorToShow);
            }

            // RISPOSTA VALIDA

            // se c'è header X-Access-Token, allora è stato fatto un refresh del token JWT ed è proprio quello il nuovo token JWT
            const refreshedJwtToken = response.headers.get('X-Access-Token');
            if ( ( typeof refreshedJwtToken === 'string' ) && ( refreshedJwtToken.trim() !== '' ) ) {
                bDebug && console.log('X-Access-Token -> refreshToken');
                localStorage.setItem( STORAGE_KEYS.TOKEN, refreshedJwtToken );
                // TODO andrebbe lanciato aggiornamento token nello stato (setAuthTokens)
                bDebug && console.log( 'Aggiornato JWT Token in localstorage', refreshedJwtToken === localStorage.getItem( STORAGE_KEYS.TOKEN ), truncateToken(refreshedJwtToken,15) );
            }

            // se la risposta è valida ed è un BLOB
            if ( ( response.status === 200 ) && type ) {
                
                if ( type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ) {
                    const responseBLOB = await response.blob();
                    responseToReturn   = new Blob([ responseBLOB ], { type });
                    
                } else if ( type === 'text/csv;charset=utf-8' ) {
                    const responseTEXT = await response.text();
                    responseToReturn   = new Blob([ encode + responseTEXT ], { type });
                    
                } else {
                    console.error('wrong type');
                    throw new Error('wrong type');
                }

            // altrimenti se il tipo (che ci si aspetta) è proprio JSON 
            // OPPURE è BLOB ma lo status è tra 201 e 299 (cioè è valido, ma non è andato a buon fine)
            // in tutti gli altri casi, se lo status NON è tra 200 (compreso) e 300 (escluso), quindi esempio se è 500
            // tentiamo comunque di estrarre e leggere un eventuale errore fornito, altrimenti forziamo un errore
            } else {

                try {
                    responseToReturn   = await response.json();
                } catch (e) {
                    console.error(`for url ${ url } response status ${ response.status }`);
                    throw new Error(e);
                }

                // se la risposta dal backend è un oggetto che contiene errorToShow o error allora è un errore
                // negli altri casi viene restituita direttamente la risposta (quindi string, number, array, o object)
                if ( responseToReturn?.errorToShow || responseToReturn?.error ) {

                    if ( typeof responseToReturn?.error !== 'string' ) {
                        // lo stampo subito qui perché dopo viene convertito in stringa per essere passato come errore
                        console.error( responseToReturn?.error );
                    }

                    sExpectedErrorToShow    = (
                        responseToReturn?.errorToShow ? (  responseToReturn?.errorToShow + '' ) : // ha sempre priorità "errorToShow" se c'è
                        responseToReturn?.error       ? (  responseToReturn?.error       + '' ) :
                                                        sDefaultErrorToShow
                    );

                    throw new Error( responseToReturn?.error + '' );

                }

            }

        } catch (error) {
            responseToReturn = '';
            isErrorHandlerNotNull && console.error( error ); // se è stato passato un ErrorHandler, stampo anche in console l'errore
            // ( sExpectedErrorToShow && ( typeof sExpectedErrorToShow === 'string' ) ) && console.error( sExpectedErrorToShow );
            displayErrorHandler( sExpectedErrorToShow || sDefaultErrorToShow );
        }

        // viene restituita direttamente la risposta (string, number, array, o object)
        return responseToReturn;

    }

}
